import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { PropTypes } from 'prop-types';

import useScrollToElement from '@zenbusiness/application-commons-hooks/useScrollToElement';
import Divider from '@zenbusiness/zui/Divider';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import useCheckoutStepViewed from '#client/hooks/clickstream/useCheckoutStepViewed';

import Card from './Card';
import { CheckoutCardHeader } from './CheckoutCardHeader';
import { CHECKOUT } from '../../constants';
import { useCheckoutContext } from '../../hooks/useCheckoutContext';
import { useCheckoutSteps } from '../../hooks/useCheckoutSteps';

const CheckoutCardContext = createContext();
CheckoutCardContext.displayName = 'CheckoutCardContext';

const useCheckoutCardContext = () => {
  const context = useContext(CheckoutCardContext);
  if (!context) {
    throw new Error(
      'CheckoutCard compound components cannot be rendered outside the CheckoutCard component'
    );
  }
  return context;
};

const CheckoutCardActive = ({ children }) => {
  const [{ stepStatus }] = useCheckoutContext();
  const step = useCheckoutCardContext();
  const stepOrder = useCheckoutSteps();
  const trackCheckoutStepViewed = useCheckoutStepViewed();
  const cardRef = useRef(null);
  const theme = useTheme();
  // Use of media query to get scroll handled by window
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scrollToElement = useScrollToElement({ offsetTop: parseInt(theme.spacing(1), 10) });

  const isStepActive = useMemo(
    () => stepStatus[step] === CHECKOUT.STEP_STATUS.ACTIVE,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepStatus[step]]
  );

  const focusActiveCardOnDesktop = (card) => {
    // Different from mobile, scroll is handled by #app-container-main wrapper
    const appContainer = document.querySelector('#app-container-main');
    if (appContainer) {
      // Get the card's position relative to the scroll container
      const cardRect = card.getBoundingClientRect();
      const containerRect = appContainer.getBoundingClientRect();
      const offsetTop = parseInt(theme.spacing(1), 10); // Same offset as mobile

      // Calculate scroll position with offset
      const scrollTop = cardRect.top - containerRect.top - offsetTop + appContainer.scrollTop;

      // Scroll with smooth behavior
      appContainer.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    } else {
      // Fallback to default scrollIntoView if container not found
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const focusActiveCard = () => {
    if (step === CHECKOUT.STEP.CART) return;

    // Use setTimeout to ensure the DOM is ready
    setTimeout(() => {
      const card = cardRef.current;
      if (card) {
        if (isMobile) {
          scrollToElement(card);
        } else {
          focusActiveCardOnDesktop(card);
        }
      }
    }, 200);
  };

  useLayoutEffect(() => {
    if (isStepActive) {
      focusActiveCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepActive]);

  useEffect(
    () => {
      if (isStepActive) {
        const stepNumber = stepOrder.indexOf(step) + 1;
        trackCheckoutStepViewed({ step: stepNumber, stepName: step });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isStepActive]
  );

  if (!isStepActive) {
    return null;
  }
  return (
    <Card
      ref={cardRef}
      data-testid="checkout-card-active"
      elevation={5}
      id={`${step}-checkout-card`}
      sx={{ zIndex: 1 }}
    >
      <CheckoutCardHeader step={step} />
      <Divider />
      {children}
    </Card>
  );
};

const CheckoutCardComplete = ({ children, hideEdit }) => {
  const [{ stepStatus }] = useCheckoutContext();
  const step = useCheckoutCardContext();

  if (stepStatus[step] !== CHECKOUT.STEP_STATUS.COMPLETE) {
    return null;
  }
  return (
    <Card data-testid="checkout-card-complete" id={`${step}-checkout-card`}>
      <CheckoutCardHeader hideEdit={hideEdit} step={step} />
      <Divider />
      {children}
    </Card>
  );
};
CheckoutCardComplete.propTypes = {
  hideEdit: PropTypes.bool
};

const CheckoutCardIncomplete = () => {
  const [{ stepStatus }] = useCheckoutContext();
  const step = useCheckoutCardContext();

  if (stepStatus[step] !== CHECKOUT.STEP_STATUS.INCOMPLETE) {
    return null;
  }
  return (
    <Card data-testid="checkout-card-incomplete" id={`${step}-checkout-card`}>
      <CheckoutCardHeader step={step} />
    </Card>
  );
};

export const CheckoutCard = ({ children, step }) => (
  <CheckoutCardContext.Provider value={step}>{children}</CheckoutCardContext.Provider>
);

CheckoutCard.Active = CheckoutCardActive;
CheckoutCard.Complete = CheckoutCardComplete;
CheckoutCard.Incomplete = CheckoutCardIncomplete;

CheckoutCard.propTypes = {
  children: PropTypes.node.isRequired,
  step: PropTypes.oneOf(Object.values(CHECKOUT.STEP)).isRequired
};
